//#region IMPORT
import Commons from '../../../Services/Common/Common';
import Global from '../../../Services/Common/Global';
import { showSnackbar } from '../../../Reducer/SnackbarReducer';
import dayjs from 'dayjs';
import { apiRequest } from '../../../Services/Common/ApiRequest';
//#endregion

/**
 * 교육 및 훈련 - TBM(CUSTOM) (TRA_CUSTOM_02) 전용 로직
 * other 케이스에서 사용
 */

// 이미지를 base64로 변환하는 함수
const getBase64ImageSafe = async (url) => {
  try {
    if (!url || typeof url !== 'string' || url.trim() === '') {
      return null;
    }

    const fullUrl = url.startsWith('http')
      ? url
      : `${Global.file.useUrl}files/${Commons.EncodeUrl(url)}`;

    const response = await fetch(fullUrl, {
      method: 'GET',
      cache: 'no-cache',
    });

    if (!response.ok) {
      console.warn('파일 없음 또는 접근 불가:', fullUrl, response.status);
      return null;
    }

    // Content-Type 확인
    const contentType = response.headers.get('content-type');
    if (!contentType || !contentType.startsWith('image/')) {
      console.warn('이미지 파일이 아님:', fullUrl, contentType);
      return null;
    }

    const blob = await response.blob();

    // Blob 크기 확인 (너무 큰 파일 방지)
    if (blob.size === 0) {
      console.warn('빈 파일:', fullUrl);
      return null;
    }

    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  } catch (e) {
    console.warn('이미지 변환 오류:', e);
    return null;
  }
};

// 교육 및 훈련 - TBM(CUSTOM) 인쇄 함수
export const PrintLogic1 = async (
  documentData, // 선택된 항목 데이터
  dispatch,
  setLoading,
  rowData1, // 전체 리스트 (사용하지 않음)
  searchRef
) => {
  setLoading(true);
  let tableData = [];
  let attendeeTableData = []; // 참석자명단 테이블 데이터

  try {
    // 선택된 데이터 확인 - documentData가 객체일 수 있으므로 배열로 변환
    const selectedRowData1 = Array.isArray(documentData)
      ? documentData
      : documentData
        ? [documentData]
        : [];

    if (!selectedRowData1 || selectedRowData1.length === 0) {
      dispatch(
        showSnackbar({
          message: '인쇄할 교육현황 항목을 선택하세요.',
          type: 'warning',
        })
      );
      return;
    } else if (selectedRowData1.length > 1) {
      dispatch(
        showSnackbar({
          message: '하나의 항목만 인쇄 가능합니다.',
          type: 'warning',
        })
      );
      return;
    }

    // 검색 연월 추출 (currentYear, month 사용)
    const currentYear = searchRef?.current?.currentYear;
    const currentMonth = searchRef?.current?.month;
    let year = '';
    let month = '';

    if (currentYear && currentMonth) {
      year = currentYear.toString();
      month = currentMonth.toString();
    } else {
      // 날짜가 없으면 현재 연월 사용
      const now = dayjs();
      year = now.format('YYYY');
      month = now.format('MM');
    }

    // 일자 포맷팅 함수 (YYYY-MM-DD)
    const FormatDate = (dateValue) => {
      if (!dateValue) return '';

      const date = new Date(dateValue);
      if (isNaN(date.getTime())) return '';

      const year = date.getFullYear();
      const month = (date.getMonth() + 1).toString().padStart(2, '0');
      const day = date.getDate().toString().padStart(2, '0');

      return `${year}-${month}-${day}`;
    };

    // 시간 포맷팅 함수 (HH:mm)
    const FormatTime = (dateValue) => {
      if (!dateValue) return '';

      const date = new Date(dateValue);
      if (isNaN(date.getTime())) return '';

      const hours = date.getHours().toString().padStart(2, '0');
      const minutes = date.getMinutes().toString().padStart(2, '0');

      return `${hours}:${minutes}`;
    };

    // 교육시간 포맷팅 함수 (시작시간 ~ 종료시간)
    const FormatEducationTime = (startTime, endTime) => {
      const start = FormatTime(startTime);
      const end = FormatTime(endTime);

      if (!start && !end) return '';
      if (start && end) return `${start} ~ ${end}`;
      if (start) return start;
      if (end) return end;
      return '';
    };

    // 부서명 조합 함수 (DEP_NM1, DEP_NM2, DEP_NM3, DEP_NM4 중 가장 하위 부서명)
    const getDepartmentName = (dep) => {
      if (dep?.depNm4 && dep.depNm4.trim() !== '') return dep.depNm4;
      if (dep?.depNm3 && dep.depNm3.trim() !== '') return dep.depNm3;
      if (dep?.depNm2 && dep.depNm2.trim() !== '') return dep.depNm2;
      if (dep?.depNm1 && dep.depNm1.trim() !== '') return dep.depNm1;
      if (dep?.depNm && dep.depNm.trim() !== '') return dep.depNm;
      return '';
    };

    // 첫 번째 항목의 회의일시와 회의장소 추출 (HeaderData용)
    let meetingYear = '';
    let meetingMonth = '';
    let meetingDay = '';
    let meetingStartTime = '';
    let meetingEndTime = '';
    let firstMeetingAddress = '';
    if (selectedRowData1.length > 0 && selectedRowData1[0]) {
      const firstRow = selectedRowData1[0];
      if (firstRow.tbmDate) {
        const date = new Date(firstRow.tbmDate);
        if (!isNaN(date.getTime())) {
          meetingYear = date.getFullYear().toString().slice(-2); // 뒤 2자리만
          meetingMonth = (date.getMonth() + 1).toString().padStart(2, '0');
          meetingDay = date.getDate().toString().padStart(2, '0');
        }
      }
      meetingStartTime = FormatTime(firstRow.tbmStartDate);
      meetingEndTime = FormatTime(firstRow.tbmEndDate);
      firstMeetingAddress = firstRow.tbmAddress || '';
    }

    // selectedRowData1의 각 항목에 대해 반복하여 조회 및 인쇄
    for (let index = 0; index < selectedRowData1.length; index++) {
      const selectedRow = selectedRowData1[index];
      if (!selectedRow.tbmCd) continue;

      const row = selectedRow;
      let attendeeListData = [];

      const result = await apiRequest(
        'post',
        Global.urls.DocsManagement.getTraCustom05R2,
        {
          TbmCd: Commons.EncryptAES(row.tbmCd) || '',
        }
      );

      const attendeeData = result.data;

      if (attendeeData && attendeeData.length > 0) {
        // 참석자 정보를 비동기로 처리하여 서명 사진을 base64로 변환
        const processedAttendees = await Promise.all(
          attendeeData.map(async (attendee) => {
            // 서명 사진 경로 확인 (FILE_PATH, SIGNATURE_PATH, CHECK_PATH 등)
            const signaturePath = attendee.signLocation || '';

            // 서명 사진을 base64로 변환
            let signatureBase64 = '';
            if (signaturePath) {
              try {
                const signatureUrl = `${Global.file.useUrl}files/${signaturePath}`;
                const base64Full = await getBase64ImageSafe(signatureUrl);
                if (base64Full && base64Full.startsWith('data:image/')) {
                  signatureBase64 = base64Full.replace(
                    /^data:image\/[a-z]+;base64,/,
                    ''
                  );
                }
              } catch (signatureError) {}
            }

            const depNm = getDepartmentName(attendee) || '';
            const userNm =
              attendee.name || attendee.USER_NM || attendee.userNm || '';

            return {
              depNm: depNm,
              userNm: userNm,
              signature: signatureBase64,
            };
          })
        );

        // 참석자명단을 좌우로 분리
        const midPoint = Math.ceil(processedAttendees.length / 2);
        const leftList = processedAttendees.slice(0, midPoint); // 좌측
        const rightList = processedAttendees.slice(midPoint); // 우측

        // 두 리스트 중 길이가 긴 값 기준으로 행 생성
        const maxLength = Math.max(leftList.length, rightList.length);

        // 참석자명단 테이블 데이터에 좌우 분리하여 추가
        for (let i = 0; i < maxLength; i++) {
          attendeeTableData.push({
            TBM_CD: row.tbmCd || '', // 교육코드 (참조용)
            TBM_TITLE: row.tbmTitle || '', // 교육명 (참조용)
            // 좌측 참석자
            DEP_NM1: leftList[i]?.depNm || '', // 소속/직책 (좌측)
            USER_NM1: leftList[i]?.userNm || '', // 성 명 (좌측)
            SIGNATURE1: leftList[i]?.signature || '', // 서 명 (좌측, base64)
            // 우측 참석자
            DEP_NM2: rightList[i]?.depNm || '', // 소속/직책 (우측)
            USER_NM2: rightList[i]?.userNm || '', // 성 명 (우측)
            SIGNATURE2: rightList[i]?.signature || '', // 서 명 (우측, base64)
          });
        }

        // 기존 호환성을 위해 attendeeListData에도 저장
        attendeeListData = processedAttendees;
      }

      // 2. 회의사진 base64 변환
      let photoBase64 = '';
      if (row.tbmPhoto) {
        try {
          const photoUrl = `${Global.file.useUrl}files/${Commons.EncodeUrl(
            row.tbmPhoto
          )}`;
          const base64Full = await getBase64ImageSafe(photoUrl);
          if (base64Full && base64Full.startsWith('data:image/')) {
            photoBase64 = base64Full.replace(/^data:image\/[a-z]+;base64,/, '');
          }
        } catch (photoError) {
          console.warn('회의사진 변환 오류:', photoError);
        }
      }

      const formattedRow = {
        NO: row.no || index + 1, // 순번
        TBM_TITLE: row.tbmTitle || '', // 교육명
        TBM_DATE: FormatDate(row.tbmDate), // 교육일시
        EDUCATION_TIME: FormatEducationTime(row.tbmStartDate, row.tbmEndDate), // 교육시간 (시작시간 ~ 종료시간)
        TBM_ADDRESS: row.tbmAddress || '', // 교육장소
        REMARK: row.remark || '', // 비고
        TBM_COMMENT: row.tbmComment || '', // 회의내용
        TBM_PHOTO: photoBase64, // 회의사진 (base64)
        ATTENDEE_LIST: attendeeListData, // 참석자명단 (좌우 2컬럼 형식)
      };

      tableData.push(formattedRow);
    }

    const printData = {
      FileName: 'TRA_BASIC_02',
      HeaderData: JSON.stringify({
        YEAR: year,
        MONTH: month,
        MEETING_YEAR: meetingYear, // 회의일시 - 연도 (뒤 2자리)
        MEETING_MONTH: meetingMonth, // 회의일시 - 월
        MEETING_DAY: meetingDay, // 회의일시 - 일
        MEETING_START_TIME: meetingStartTime, // 회의일시 - 시작시간 (HH:mm)
        MEETING_END_TIME: meetingEndTime, // 회의일시 - 종료시간 (HH:mm)
        TBM_ADDRESS: firstMeetingAddress, // 회의장소
      }),
      TableData: JSON.stringify(tableData), // 교육현황 테이블 데이터
      TableData2: JSON.stringify(attendeeTableData), // 참석자명단 테이블 데이터 (DEP_NM, USER_NM, SIGNATURE 컬럼)
    };

    const response = await Commons.Axios(
      'report',
      Global.base.useUrl + Global.urls.Reports.reports,
      printData
    );

    // response.data가 blob인 경우 그대로 반환
    if (response.data instanceof Blob) {
      return response.data;
    }
    // response.data가 다른 형태인 경우 blob으로 변환
    else if (response.data) {
      return new Blob([response.data], { type: 'application/pdf' });
    } else {
      console.error('PrintLogic1: 응답 데이터가 없습니다.');
      return null;
    }
  } catch (e) {
    console.log(e);
    dispatch(showSnackbar({ message: e.message, type: 'error' }));
    return null;
  } finally {
    setLoading(false);
  }
};
